import { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  IconButton,
  Tooltip,
  Typography,
  Alert,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useTranslation } from 'react-i18next';
import { dashboardApi } from '@/core/api';
import type { ResponseDashboardByAdjusterDTO } from '@/core/api';
import { fromAxiosError } from '@/core/api';
import { daysAgoIso, todayIso } from '@/core/utils';
import { BackToTopFab, HelpAlert, usePageTitle } from '@/shared';
import KpiCard from './KpiCard';
import KpiCardSkeleton from './KpiCardSkeleton';
import { kpiVisual, sectionEyebrowSx } from './kpiVisuals';
import KpiIcon from './KpiIcon';

export default function AdjusterHome() {
  const { t } = useTranslation();
  usePageTitle('home.adjuster.pageTitle');

  const [data, setData] = useState<ResponseDashboardByAdjusterDTO | null>(null);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [version, setVersion] = useState(0);

  useEffect(() => {
    let cancelled = false;

    setLoading(true);
    setErrorMessage(null);

    async function fetchDashboard() {
      try {
        const result = await dashboardApi.getAdjusterDashboard();
        if (!cancelled) {
          setData(result);
        }
      } catch (err) {
        if (!cancelled) {
          setErrorMessage(fromAxiosError(err).message);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    void fetchDashboard();

    return () => {
      cancelled = true;
    };
  }, [version]);

  if (errorMessage !== null) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          <strong>{t('home.errorTitle')}: </strong>
          {errorMessage}
        </Alert>
      </Box>
    );
  }

  const ns = 'HOME.DASHBOARD.ADJUSTER';

  return (
    <Box sx={{ p: 3 }}>
      {/* Page header — sticky so title and refresh controls stay accessible on scroll */}
      <Box
        sx={{
          position: 'sticky',
          top: 0,
          bgcolor: 'background.default',
          zIndex: 1,
          py: 1,
          mb: 2,
        }}
      >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Typography variant="h5" component="h1" sx={{ color: 'primary.main' }}>
          {t('home.adjuster.pageTitle')}
        </Typography>
        <Tooltip title={t('actions.refresh')}>
          <span>
            <IconButton
              aria-label={t('actions.refresh')}
              size="small"
              onClick={() => setVersion((v) => v + 1)}
              disabled={loading}
              sx={{ '@media print': { display: 'none' } }}
            >
              <RefreshIcon />
            </IconButton>
          </span>
        </Tooltip>
      </Box>
      </Box>

      <HelpAlert
        persistKey="home.adjuster.welcome"
        severity="info"
        variant="outlined"
        title={t('HELP.home.adjuster.welcome.title')}
      >
        {t('HELP.home.adjuster.welcome.body')}
      </HelpAlert>

      {/* ------------------------------------------------------------------ */}
      {/* Section 1 — Main (5 KPI cards, no title — no main.title key exists) */}
      {/* ------------------------------------------------------------------ */}
      <Grid container spacing={2} sx={{ mb: 4 }} aria-busy={loading || !data}>
        {loading || !data
          ? Array.from({ length: 5 }).map((_, i) => (
              <Grid item xs={12} sm={6} md={4} lg={2.4} key={i}>
                <KpiCardSkeleton />
              </Grid>
            ))
          : (
            [
              // Only tiles whose drill-down reproduces the tile's own number get
              // a link. `ClaimsBLL` already scopes every claims query to the
              // signed-in adjuster (`adjusterId == sourceType`), so /claims
              // filtered by status returns exactly what this card counted.
              //
              // The two document-request tiles now qualify. They did not
              // before: they counted only `onlyViewAdjuster` rows while
              // `GetDocumentRequests` scopes an adjuster by claim alone, so the
              // list behind them would have shown more than the card promised.
              // That mismatch is fixed in `DashboardBLL`, and the tabbed list
              // takes `?status=`, so both now land on exactly what they counted.
              //
              // The 7-day tile still does not: the list has no date filter, so
              // it would land on the all-time total under a card that counted a
              // week. Left summary-only rather than pointing somewhere that
              // disagrees with it.
              //
              // Unread messages links to the inbox. It is the one exception to
              // the rule above, and deliberately: the conversations page shows
              // per-thread unread counts rather than a single competing total,
              // so there is no number there to contradict this one — and a
              // count of unread messages that cannot be clicked is telling
              // somebody they have mail and hiding the mailbox.
              { key: 'unreadMessages',                value: data.main.unreadMessages,                linkTo: '/catalogs/administrator-chat' },
              { key: 'openClaims',                    value: data.main.openClaims,                    linkTo: '/claims?status=OPEN' },
              { key: 'openDocumentRequest',           value: data.main.openDocumentRequest,           linkTo: '/document-requests?status=OPEN' },
              { key: 'pendingDocumentRequest',        value: data.main.pendingDocumentRequest,        linkTo: '/document-requests?status=PENDING' },
              { key: 'pendingDocumentRequestLast7Days', value: data.main.pendingDocumentRequestLast7Days, linkTo: undefined },
            ] satisfies Array<{ key: string; value: number; linkTo: string | undefined }>
          ).map(({ key, value, linkTo }) => (
            <Grid item xs={12} sm={6} md={4} lg={2.4} key={key}>
              <KpiCard
                label={t(`${ns}.main.${key}`)}
                value={value}
                icon={<KpiIcon kpiKey={key} />}
                tone={kpiVisual(key).tone}
                linkTo={linkTo}
              />
            </Grid>
          ))
        }
      </Grid>

      {/* ------------------------------------------------------------------ */}
      {/* Section 2 — Last Week (4 KPI cards)                                */}
      {/* ------------------------------------------------------------------ */}
      <Typography
        id="adjuster-home-week-title"
        component="h2"
        sx={sectionEyebrowSx}
      >
        {t(`${ns}.week.title`)}
      </Typography>
      <Grid
        container
        spacing={2}
        aria-busy={loading || !data}
        role="region"
        aria-labelledby="adjuster-home-week-title"
      >
        {loading || !data
          ? Array.from({ length: 4 }).map((_, i) => (
              <Grid item xs={12} sm={6} md={3} lg={3} key={i}>
                <KpiCardSkeleton />
              </Grid>
            ))
          : (
            [
              // Both claim tiles count a 7-day `claimDate` window, which is what
              // the non-dashboard `/claims` date filter matches on.
              { key: 'newClaims',                value: data.week.newClaims,               linkTo: `/claims?${new URLSearchParams({ startDate: daysAgoIso(7), endDate: todayIso() }).toString()}` },
              { key: 'closedClaims',             value: data.week.closedClaims,            linkTo: `/claims?${new URLSearchParams({ status: 'CLOSE', startDate: daysAgoIso(7), endDate: todayIso() }).toString()}` },
              { key: 'approveDocumentRequest',   value: data.week.approveDocumentRequest,  linkTo: undefined },
              { key: 'rejectedDocumentRequest',  value: data.week.rejectedDocumentRequest, linkTo: undefined },
            ] satisfies Array<{ key: string; value: number; linkTo: string | undefined }>
          ).map(({ key, value, linkTo }) => (
            <Grid item xs={12} sm={6} md={3} lg={3} key={key}>
              <KpiCard
                label={t(`${ns}.week.${key}`)}
                value={value}
                icon={<KpiIcon kpiKey={key} />}
                tone={kpiVisual(key).tone}
                linkTo={linkTo}
              />
            </Grid>
          ))
        }
      </Grid>
      <BackToTopFab />
    </Box>
  );
}
